/**
 * ErrorState — v2 상태 화면 S3 (통신 에러).
 * 다시 시도 + 전화 문의 + 오류코드·시각 (docs/design-v2/부냥 상태 변형 v2.html S3).
 * 전화는 tel: 링크만(외부 링크 없음). 번호 기본값 = 대표번호.
 */

import React from 'react';
import { DEFAULT_CONTACT_PHONE, type ContactPhone } from '../../domain/inquiry/resolveContactPhone';

export interface ErrorStateProps {
  onRetry?: () => void;
  /** 오류코드 — 예: 'E-503', 'E-NET' */
  code?: string;
  /** 발생 시각 (미지정 시 렌더 시점) */
  at?: Date;
  phone?: Pick<ContactPhone, 'display' | 'tel'>;
  title?: string;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
export const formatErrorTime = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** axios 등 에러 객체 → 'E-503' / 'E-NET' */
export function errorCodeOf(err: unknown): string {
  const status = (err as any)?.response?.status;
  if (typeof status === 'number') return `E-${status}`;
  return 'E-NET';
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  onRetry,
  code = 'E-NET',
  at,
  phone = DEFAULT_CONTACT_PHONE,
  title = '정보를 불러오지 못했습니다',
  className = '',
}) => {
  const time = formatErrorTime(at ?? new Date());
  return (
    <div
      role="alert"
      className={`flex flex-col items-center gap-2.5 px-8 py-14 text-center ${className}`}
      data-testid="error-state"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-bad-bg text-bad">
        <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M22.61 16.95A5 5 0 0 0 18 10h-1.26a8 8 0 0 0-7.05-6M5 5a8 8 0 0 0 4 15h9a5 5 0 0 0 1.7-.3" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      </div>
      <b className="text-[18px] font-bold leading-[26px] text-ink">{title}</b>
      <p className="text-[14px] leading-5 text-muted">
        네트워크 연결을 확인한 뒤 다시 시도해 주세요.
        <br />
        문제가 계속되면 전화로 문의하실 수 있습니다.
      </p>
      <div className="mt-1.5 flex w-full max-w-[320px] flex-col gap-2">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="flex h-11 w-full items-center justify-center gap-1.5 rounded-ca-xl bg-accent text-[14px] font-bold text-white shadow-ca-cta hover:bg-accent-dark"
          >
            다시 시도
          </button>
        )}
        <a
          href={phone.tel}
          className="num flex h-11 w-full items-center justify-center gap-1.5 rounded-ca-xl bg-ca-surface text-[14px] font-bold text-ink"
        >
          {phone.display} 전화 문의
        </a>
      </div>
      <span className="num mt-2 text-[11px] font-bold text-mute2">
        오류코드 {code} · {time}
      </span>
    </div>
  );
};

export default ErrorState;
